import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_MANAGER_NETWORK } from "@/lib/seo-defs";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Star, Briefcase, Award, Filter } from "lucide-react";

const ITALIAN_REGIONS = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia",
  "Toscana", "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];

const ROLES = [
  "Fractional CEO / General Manager",
  "Fractional COO / Operations Manager",
  "Fractional CFO / Finance Manager",
  "Fractional CTO / Digital Transformation Manager",
  "Fractional CMO / Marketing & Sales Manager",
  "Fractional CHRO / HR & Organization Manager",
  "Fractional CSO / Strategy Manager",
  "Sustainability & Energy Manager",
];

export default function ManagerNetworkPage() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ role: "", region: "", skill: "", premium_only: false });
  const [total, setTotal] = useState(0);

  const fetchManagers = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.role) params.set("role", filters.role);
    if (filters.region) params.set("region", filters.region);
    if (filters.skill) params.set("skill", filters.skill);
    if (filters.premium_only) params.set("premium_only", "true");
    try {
      const r = await api.get(`/marketplace/managers?${params.toString()}&limit=48`);
      setManagers(r.data?.managers || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchManagers(); }, []);

  const reset = () => {
    setFilters({ role: "", region: "", skill: "", premium_only: false });
    setTimeout(fetchManagers, 50);
  };

  return (
    <div className="min-h-screen">
      <SEO {...SEO_MANAGER_NETWORK} />
      <PublicHeader />
      <section className="pt-28 pb-12 bg-gradient-to-b from-[#EAF0FF]/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Network Marketplace</Badge>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Esplora i manager CXOORA
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Esperti C-level disponibili in modalità Fractional. Filtra per ruolo, regione, competenze e contattali tramite CXOORA.
          </p>

          {/* Filters */}
          <div className="mt-6 cxo-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter size={16} className="text-slate-500" />
              <h3 className="text-sm font-semibold text-slate-700">Filtri</h3>
              <span className="ml-auto text-xs text-slate-500">{total} manager nel network</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Select value={filters.role || "all"} onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-role"><SelectValue placeholder="Ruolo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i ruoli</SelectItem>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.region || "all"} onValueChange={(v) => setFilters({ ...filters, region: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-region"><SelectValue placeholder="Regione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le regioni</SelectItem>
                  {ITALIAN_REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                placeholder="Cerca competenza..."
                value={filters.skill}
                onChange={(e) => setFilters({ ...filters, skill: e.target.value })}
                data-testid="filter-skill"
              />
              <div className="flex gap-2">
                <Button onClick={fetchManagers} className="flex-1 bg-[#2D6BFF] text-white gap-1.5" data-testid="filter-apply">
                  <Search size={14} /> Cerca
                </Button>
                <Button variant="outline" onClick={reset} data-testid="filter-reset">Reset</Button>
              </div>
            </div>
          </div>

          {/* Grid */}
          <div className="mt-6">
            {loading ? (
              <p className="text-center text-slate-500 py-12">Caricamento...</p>
            ) : managers.length === 0 ? (
              <div className="cxo-card p-12 text-center text-slate-500">
                <p>Nessun manager trovato con questi filtri.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {managers.map((m) => <ManagerCard key={m.id} m={m} />)}
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-slate-600">Sei un manager? Unisciti al network.</p>
            <Link to="/per-i-manager/candidati"><Button variant="outline" className="mt-2">Candidati ora</Button></Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const ManagerCard = ({ m }) => {
  const photoUrl = m.photo?.download_url
    ? `${API_BASE}${m.photo.download_url}?inline=true`
    : null;
  return (
    <div className="cxo-card p-5 hover:shadow-lg transition-shadow" data-testid={`mgr-card-${m.id}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#EAF0FF] to-white border-2 border-[#2D6BFF]/15 text-[#2D6BFF] font-display font-bold text-lg shrink-0">
          {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover rounded-full" /> : (m.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-slate-900 truncate">{m.full_name}</h3>
          <p className="text-xs text-slate-600 truncate">{m.title || m.primary_role}</p>
        </div>
        {m.is_premium && (
          <Badge className="bg-[#7c3aed]/10 text-[#7c3aed] border-0 text-[10px] gap-1 shrink-0"><Award size={10} /> Premium</Badge>
        )}
      </div>
      <p className="text-xs text-slate-600 mt-3 line-clamp-2 min-h-[32px]">{m.bio_short || "—"}</p>

      <div className="mt-3 flex flex-wrap gap-1">
        {(m.skills || []).slice(0, 3).map((s, i) => (
          <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{s}</span>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-600">
        <div className="flex items-center gap-1.5"><MapPin size={12} className="text-slate-400" />{m.city || "—"}</div>
        <div className="flex items-center gap-1.5"><Briefcase size={12} className="text-slate-400" />{m.years_managerial || 0}+ anni</div>
        <div className="flex items-center gap-1.5"><Star size={12} className="text-[#B7791F]" />{m.rating_average?.toFixed(1) || "—"}</div>
        <div className="text-right font-medium text-slate-900">
          {m.daily_rate_min && m.daily_rate_max ? `€${m.daily_rate_min}-${m.daily_rate_max}/g` : "Contatta"}
        </div>
      </div>

      <Link to={`/register`} className="block mt-4">
        <Button variant="outline" className="w-full text-xs gap-1.5" data-testid={`mgr-contact-${m.id}`}>
          Contatta tramite CXOORA
        </Button>
      </Link>
    </div>
  );
};
